import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { CommonVariables } from "../../../types";

export interface BreadcrumbItem {
  label: string;
  url?: string;
  isFuture?: boolean;
}

export type BreadcrumbType = "cart" | "checkout";

interface BreadcrumbsProps {
  /** Custom items. If provided, 'type' is ignored. */
  items?: BreadcrumbItem[];
  /** Pre-defined structures for common pages. */
  type?: BreadcrumbType;
  className?: string;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  type,
  className = "",
}) => {
  const { t } = useTranslation();

  // Access global variables for links (e.g., Shop URL)
  const commonVars = window.commonVariables as CommonVariables | undefined;
  const shopUrl = commonVars?.shopUrl || "/shop";

  // Logic to generate standard paths based on 'type'
  const finalItems = useMemo(() => {
    // 1. If custom items are passed, use them directly.
    if (items) return items;

    // 2. If a 'type' is passed, generate the specific structure.
    if (type === "cart") {
      return [
        {
          label: t("breadcrumbs.shop", "Shop"),
          url: shopUrl,
        },
        {
          label: t("breadcrumbs.cart", "Cart"),
          // Current page, no URL
        },
        {
          label: t("breadcrumbs.checkout", "Checkout"),
          isFuture: true,
        },
      ];
    }

    if (type === "checkout") {
      return [
        {
          label: t("breadcrumbs.shop", "Shop"),
          url: shopUrl,
        },
        {
          label: t("breadcrumbs.cart", "Cart"),
          url: commonVars?.cartUrl || "/cart", // Dynamic URL for cart
        },
        {
          label: t("breadcrumbs.checkout", "Checkout"),
          // Current page
        },
      ];
    }

    return [];
  }, [items, type, t, shopUrl, commonVars?.cartUrl]);

  if (!finalItems || finalItems.length === 0) return null;

  return (
    <nav className={`breadcrumbs ${className}`} aria-label="Breadcrumb">
      <ol className="breadcrumbs__list">
        {finalItems.map((item, index) => {
          const isLast = index === finalItems.length - 1;
          const isFuture = !!item.isFuture;

          return (
            <li
              key={`${index}-${item.label}`}
              className={`breadcrumbs__item ${isFuture ? "is-future" : ""} ${
                isLast && !isFuture ? "is-active" : ""
              }`}
              aria-current={isLast && !isFuture ? "page" : undefined}
            >
              {item.url && !isLast && !isFuture ? (
                <a href={item.url} className="breadcrumbs__link">
                  {item.label}
                </a>
              ) : (
                <span className="breadcrumbs__text">{item.label}</span>
              )}

              {!isLast && <span className="breadcrumbs__separator">/</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
